import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Box, Stack, TextField, ToggleButton } from "@material-ui/core";
import {
  FormatBold as BoldIcon,
  FormatUnderlined as UnderlineIcon,
  FormatListBulleted as BulletsIcon,
  FormatListNumbered as NumbersIcon
} from "@material-ui/icons";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";

/**
 * Send-form input for the additional-terms block: an editable section title
 * (rendered underlined in the document, so the input shows it underlined
 * too) plus a deliberately MINIMAL rich-text editor — bold, underline,
 * bullets, numbered lists. Nothing else: the terms are legal clauses, not a
 * layout surface, and every extra mark is one more thing the send renderer
 * must reproduce pixel-faithfully.
 *
 * The component owns no value state — the parent keeps `title` and receives
 * the terms HTML on every edit via `onTermsChange(html, isEmpty)`. `isEmpty`
 * is TipTap's own emptiness (an empty paragraph counts as empty), which the
 * parent uses to decide whether the send even carries an additionalTerms
 * payload.
 */
export type AdditionalTermsInputProps = {
  title: string;
  onTitleChange: (next: string) => void;
  onTermsChange: (html: string, isEmpty: boolean) => void;
  /**
   * Terms HTML to seed the editor with on MOUNT (edit-after-send prefill).
   * Captured once by useEditor — later changes are ignored, matching the
   * component's "owns no value state" contract. The parent must set its own
   * terms state alongside (onUpdate does not fire for initial content).
   */
  initialContent?: string;
};

export default function AdditionalTermsInput({
  title,
  onTitleChange,
  onTermsChange,
  initialContent
}: AdditionalTermsInputProps): JSX.Element {
  const { t } = useTranslation();

  // useEditor captures its config once — route onUpdate through a ref so
  // the freshest parent callback runs without re-creating the editor.
  const onTermsChangeRef = useRef(onTermsChange);
  onTermsChangeRef.current = onTermsChange;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Only what the toolbar offers survives — everything else off so
        // keyboard shortcuts / markdown-style input rules can't sneak in
        // marks the send renderer was never designed to carry.
        heading: false,
        blockquote: false,
        code: false,
        codeBlock: false,
        horizontalRule: false,
        strike: false,
        italic: false
      }),
      Underline
    ],
    content: initialContent || "",
    onUpdate: ({ editor: ed }) => onTermsChangeRef.current(ed.getHTML(), ed.isEmpty)
  });

  const controls: {
    key: string;
    Icon: typeof BoldIcon;
    isActive: boolean;
    run: () => void;
    titleKey: string;
  }[] = editor
    ? [
        {
          key: "bold",
          Icon: BoldIcon,
          isActive: editor.isActive("bold"),
          run: () => editor.chain().focus().toggleBold().run(),
          titleKey: "signatures.additionalTerms.bold"
        },
        {
          key: "underline",
          Icon: UnderlineIcon,
          isActive: editor.isActive("underline"),
          run: () => editor.chain().focus().toggleUnderline().run(),
          titleKey: "signatures.additionalTerms.underline"
        },
        {
          key: "bulletList",
          Icon: BulletsIcon,
          isActive: editor.isActive("bulletList"),
          run: () => editor.chain().focus().toggleBulletList().run(),
          titleKey: "signatures.additionalTerms.bullets"
        },
        {
          key: "orderedList",
          Icon: NumbersIcon,
          isActive: editor.isActive("orderedList"),
          run: () => editor.chain().focus().toggleOrderedList().run(),
          titleKey: "signatures.additionalTerms.numbers"
        }
      ]
    : [];

  return (
    <Stack spacing={1.5}>
      <TextField
        size="small"
        label={t("signatures.additionalTerms.sectionTitleLabel")}
        value={title}
        onChange={(e) => onTitleChange(e.target.value)}
        // The title stamps underlined into the document — previewing that in
        // the input keeps "what you type is what they sign" honest.
        inputProps={{ style: { textDecoration: "underline", fontWeight: 600 } }}
        sx={{ maxWidth: 260 }}
      />
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          overflow: "hidden"
        }}
      >
        <Stack
          direction="row"
          spacing={0.5}
          sx={{ p: 0.5, borderBottom: "1px solid", borderColor: "divider" }}
        >
          {controls.map(({ key, Icon, isActive, run, titleKey }) => (
            <ToggleButton
              key={key}
              value={key}
              size="small"
              selected={isActive}
              // onMouseDown preventDefault keeps the editor focused while
              // clicking the toolbar — otherwise the toggle applies and the
              // caret is gone.
              onMouseDown={(e) => e.preventDefault()}
              onChange={run}
              title={t(titleKey) as string}
              sx={{ border: "none", p: 0.5 }}
            >
              <Icon fontSize="small" />
            </ToggleButton>
          ))}
        </Stack>
        <Box
          onClick={() => editor?.chain().focus().run()}
          sx={{
            p: 1,
            cursor: "text",
            // The dashboard's global reset zeroes list padding/markers —
            // restore basic list rendering inside the editor so the input
            // roughly matches how the terms render in the document.
            "& .ProseMirror": { minHeight: 96, outline: "none" },
            "& .ProseMirror p": { margin: "0 0 8px" },
            "& .ProseMirror ul, & .ProseMirror ol": {
              paddingInlineStart: "24px",
              margin: "0 0 8px"
            },
            "& .ProseMirror ul": { listStyle: "disc" },
            "& .ProseMirror ol": { listStyle: "decimal" }
          }}
        >
          <EditorContent editor={editor} />
        </Box>
      </Box>
    </Stack>
  );
}
